import { AdvancedSelect } from '../../../widgets/select';
import { FormControl } from '../../../widgets/formControl';
import { Link, TextButton } from '../../../widgets/buttons';
import { QuestionDialog, useDialogManager } from '../../../widgets/dialog';
import { SwitchGroup } from '../../../widgets/switchGroup';
import React, { FC, useEffect, useState } from 'react';
import { Unit, units } from 'src/components/widgets/areaDesigner/helpers';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Section, SettingsMenuContentProps, _NoEdgeInput } from './sharedPlacementComponents';

const FooterContainer = styled.div`
    display: flex;
    flex-direction: row-reverse;
    margin-top: 20px;
`;

const SwitchContainer = styled.div`
    margin-top: 15px;
`;

const SettingsMenuContent: FC<SettingsMenuContentProps> = ({ unit, widthCM, heightCM, dpi, name, stacked, containsLayers, onConfirm }) => {
    const [unitOfMeasure, setUnitOfMeasure] = useState<Unit>(unit);
    const [width, setWidth] = useState<number>(widthCM);
    const [height, setHeight] = useState<number>(heightCM);
    const [dotPerInch, setDotPerInch] = useState<number>(dpi);
    const [placementName, setPlacementName] = useState<string>(name);
    const [disableSave, setDisableSave] = useState<boolean>(true);

    const [isStacked, setIsStacked] = useState(stacked);

    const { showDialog, closeDialog } = useDialogManager();

    useEffect(() => {
        setWidth(width / unitOfMeasure.ratio);
        setHeight(height / unitOfMeasure.ratio);
    }, [unitOfMeasure]);

    useEffect(() => {
        if (!isNaN(width) && !isNaN(height) && !isNaN(dotPerInch) && width > 0 && height > 0 && dotPerInch > 0) setDisableSave(false);
        else setDisableSave(true);
    }, [width, height, dotPerInch]);

    // I added this useEffect because the tooltip that always coming from the right side of the screen this cause the showing of the orizontal scrollbar
    useEffect(() => {
        document.body.style.overflowX = 'hidden';
    }, []);

    const handleConfirm = () => {
        if (containsLayers && (width !== widthCM || height !== heightCM)) {
            showDialog(
                'confirm',
                <QuestionDialog
                    message={T._('Some pdf layers uploaded have a different size. Confirming they will be deleted. Do you want to proceed?', 'Admin')}
                    onYesClick={async () => {
                        closeDialog('confirm');
                        onConfirm(unitOfMeasure, width * unitOfMeasure.ratio, height * unitOfMeasure.ratio, dotPerInch, placementName, isStacked, true);
                    }}
                />
            );
        } else {
            onConfirm(unitOfMeasure, width * unitOfMeasure.ratio, height * unitOfMeasure.ratio, dotPerInch, placementName, isStacked, false);
        }
    };

    return (
        <>
            {/* name temporary*/}
            <FormControl>
                <_NoEdgeInput value={placementName} placeholder={T._('name', 'Admin')} type='text' onChange={(e) => setPlacementName(e.currentTarget.value)} />
            </FormControl>

            <Section
                label={T._('Unit of measure', 'Admin')}
                tooltip={T._(
                    'The unit of measure selected here only affects the configuration of this tool. It does not determine what the end user will see in the interface. The unit of measure displayed to users is defined as a global setting in Settings > User Interface Settings > Unit of Measure.',
                    'Admin'
                )}
            >
                <AdvancedSelect
                    isSearchable={false}
                    options={units}
                    placeholder={T._('Select unit...', 'Admin')}
                    onChange={(e) => {
                        const unit = e as (typeof units)[number];
                        setUnitOfMeasure(unit);
                    }}
                    isMulti={false}
                    styles={{
                        menu: (prev) => ({
                            ...prev,
                            width: 'auto',
                            zIndex: 3
                        }),
                        control: (provided, state) => ({
                            ...provided,
                            minWidth: '80px',
                            minHeight: '30px',
                            border: 'none',
                            borderRadius: '0',
                            borderBottom: '1px solid rgb(128, 128, 128)',
                            boxShadow: 'none',
                            '&:hover': {
                                border: 'none',
                                borderRadius: '0',
                                borderBottom: '1px solid rgb(128, 128, 128)'
                            },
                            ...(state.menuIsOpen && {
                                borderColor: 'transparent',
                                boxShadow: 'none'
                            })
                        }),
                        indicatorSeparator: () => ({
                            display: 'none'
                        }),
                        singleValue: (provided) => ({
                            ...provided,
                            padding: '0px 2px'
                        }),
                        dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: 0
                        })
                    }}
                    value={unitOfMeasure}
                />
            </Section>

            <Section label={T._('Size', 'Admin')}>
                <Section label={T._('w', 'Admin')}>
                    <_NoEdgeInput
                        value={width}
                        placeholder={T._('Width', 'Admin')}
                        min={1}
                        type='number'
                        step={1}
                        onChange={(e) => setWidth(parseFloat(e.currentTarget.value))}
                    />
                </Section>
                <Section label={T._('h', 'Admin')}>
                    <_NoEdgeInput
                        value={height}
                        placeholder={T._('Height', 'Admin')}
                        min={1}
                        type='number'
                        step={1}
                        onChange={(e) => setHeight(parseFloat(e.currentTarget.value))}
                    />
                </Section>
            </Section>

            <Section label={T._('DPI', 'Admin')}>
                <Section>
                    <_NoEdgeInput
                        value={dotPerInch}
                        placeholder={T._('DPI', 'Admin')}
                        min={1}
                        type='number'
                        step={1}
                        onChange={(e) => setDotPerInch(parseFloat(e.currentTarget.value))}
                    />
                </Section>
            </Section>
            <SwitchContainer>
                <Section label={T._(isStacked ? 'Stacked Print File' : 'Not Stacked Print File', 'Admin')}>
                    <Section>
                        <SwitchGroup
                            label={''}
                            checked={isStacked}
                            helpToolTip={
                                <>
                                    {T._(
                                        'Enabling "Stacked Print File" means that an advanced single print-ready file will be generated for all designs in your order, while enabling "Not Stacked Print File" means separate advanced print-ready files will be generated for each design in your order.',
                                        'Admin'
                                    )}
                                    <br />
                                    <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/9686184040348-Advanced-Print-Files-Builder'>
                                        {T._('Learn more here', 'Admin')}
                                    </Link>
                                </>
                            }
                            helpTooltipPosition='left'
                            onChange={(checked) => {
                                setIsStacked(checked);
                            }}
                        />
                    </Section>
                </Section>
            </SwitchContainer>
            <FooterContainer>
                <TextButton onClick={handleConfirm}>{T._('Confirm', 'Admin')}</TextButton>
            </FooterContainer>
        </>
    );
};

export default SettingsMenuContent;
